import { useState } from "react";
import { Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { ContentFramework } from "@/lib/composerCore";

const DEFAULT_COLUMN_COLOR = "indigo";

type Props = {
  workspaceId: string;
  framework: ContentFramework;
  position: number;
  onChange: () => void | Promise<void>;
};

export function EmptyColumnSlot({ workspaceId, framework, position, onChange }: Props) {
  const [creating, setCreating] = useState(false);
  const [newColumnLabel, setNewColumnLabel] = useState("");

  const handleCreate = async () => {
    const label = newColumnLabel.trim() || `Column ${position + 1}`;
    setCreating(true);
    const { error } = await supabase.from("content_framework_columns").insert({
      workspace_id: workspaceId,
      framework_id: framework.id,
      label,
      position,
      color_key: DEFAULT_COLUMN_COLOR,
    });
    setCreating(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setNewColumnLabel("");
    await onChange();
  };

  return (
    <Card className="border-dashed shadow-none">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <Badge variant="outline">Slot {position + 1}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">Empty column slot.</p>
        <Input
          placeholder="Column name"
          value={newColumnLabel}
          onChange={(event) => setNewColumnLabel(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void handleCreate();
            }
          }}
        />
        <Button size="sm" onClick={() => void handleCreate()} disabled={creating}>
          <Plus className="mr-1 h-4 w-4" />
          Add column
        </Button>
      </CardContent>
    </Card>
  );
}
